import { useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';

/**
 * useDebouncedOnChangeV2
 *
 * Returns a debounced version of the given onChange handler. The debounced
 * handler forwards all arguments to the latest onChange callback after
 * waiting for the specified debounceTime.
 *
 * Debouncing is purely time-based – identical consecutive values will still
 * result in onChange being called (after the debounce delay), as long as the
 * calls are separated in time according to debounceTime.
 *
 * @param {Function} onChange - Callback to invoke after the debounce delay.
 * @param {number} debounceTime - Debounce delay in milliseconds.
 *
 * @returns {Function} debouncedOnChange - A debounced handler suitable for use as an onChange callback.
 */
const useDebouncedOnChangeV2 = (onChange, debounceTime = 1000) => {
  const onChangeRef = useRef(onChange);

  // Always keep a reference to the latest onChange handler.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create the debounced function once per debounceTime change.
  const debouncedOnChange = useMemo(() => {
    if (typeof onChange !== 'function') {
      // No-op handler if onChange is not provided.
      return () => {};
    }

    const handler = debounce((...args) => {
      if (typeof onChangeRef.current === 'function') {
        onChangeRef.current(...args);
      }
    }, debounceTime);

    return handler;
  }, [debounceTime, onChangeRef]);

  // Cancel any pending debounced calls on unmount or when the debounced
  // function instance changes (e.g., debounceTime change).
  useEffect(() => {
    return () => {
      if (debouncedOnChange && typeof debouncedOnChange.cancel === 'function') {
        debouncedOnChange.cancel();
      }
    };
  }, [debouncedOnChange]);

  return debouncedOnChange;
};

export default useDebouncedOnChangeV2;

